import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Tooltip,
  IconButton,
  Link,
  Popper,
  Paper,
  ClickAwayListener,
  Snackbar,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useStyles } from './styles';
import { useState, useCallback, useMemo } from 'react';
import ShareIcon from '@mui/icons-material/Share';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import XIcon from '@mui/icons-material/X';
import EmailIcon from '@mui/icons-material/Email';
import TimelineIcon from '@mui/icons-material/Timeline';
import BrainIconLight from '../../assets/images/brain_icon_transparent.gif';
import BrainIconDark from '../../assets/images/brain_dark_full_transparent.gif';
import AppleIcon from '../../assets/images/apple-icon.png';
import AppleIconBlack from '../../assets/images/apple-icon-black.png';
import SpotifyIcon from '../../assets/images/spotify-icon.png';
import SpotifyIconBlack from '../../assets/images/spotify-icon-black.png';
import { ShareAchievementModal } from '../ShareAchievementModal/ShareAchievementModal';
import { setShowAnalyze } from '../../redux/slices/podcastsSlice';

const CONST = {
  EMAIL: 'email',
  FACEBOOK: 'facebook',
  X: 'X',
  LINKEDIN: 'linkedin',
};

export const PlatformsAndSocials = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { currentPodcast, selectedCountry, activePlatform, showAnalyze } = useSelector((state) => state.podcasts);
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showCopyNotification, setShowCopyNotification] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedSharePlatform, setSelectedSharePlatform] = useState(null);
  const { isLight } = useSelector((state) => state.theme);

  const baseUrl = window.location.origin;
  const shareUrl = useMemo(() => {
    const podcastId =
      activePlatform === 'spotify' && currentPodcast.spotifyUrl
        ? currentPodcast.spotifyUrl.split('/').pop()
        : currentPodcast.collectionId;

    return `${baseUrl}/api/top-podcasts/share/${podcastId}?country=${encodeURIComponent(selectedCountry)}`;
  }, [baseUrl, currentPodcast, selectedCountry, activePlatform]);

  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  const tryOpenMobileApp = (mobileUrl, webUrl) => {
    if (!isMobile()) {
      window.open(webUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    const startTime = Date.now();
    const timeout = setTimeout(() => {
      window.open(webUrl, '_blank', 'noopener,noreferrer');
    }, 1000);

    const handleVisibilityChange = () => {
      if (Date.now() - startTime > 500) {
        clearTimeout(timeout);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    window.location.href = mobileUrl;
  };

  const tryOpenFacebookApp = (shareUrl) => {
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const webUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;

    if (isIOS) {
      const mobileUrl = `fb://share?link=${encodeURIComponent(shareUrl)}`;
      tryOpenMobileApp(mobileUrl, webUrl);
    } else {
      window.open(webUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleMouseEnter = useCallback((event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setOpen(false);
  }, []);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(shareUrl);
    setShowCopyNotification(true);
    setOpen(false);
  }, [shareUrl]);

  const handleShare = useCallback((platform) => {
    setSelectedSharePlatform(platform);
    setShowShareModal(true);
    setOpen(false);
  }, []);

  const handleAnalyzeClick = useCallback(() => {
    dispatch(setShowAnalyze(true));
  }, [dispatch]);

  const handleChartsClick = useCallback(() => {
    dispatch(setShowAnalyze(false));
  }, [dispatch]);

  const handleActualShare = useCallback(() => {
    if (!selectedSharePlatform) return;

    const text = `Check out the charts for the ${currentPodcast.collectionName} podcast!`;

    switch (selectedSharePlatform) {
      case CONST.FACEBOOK: {
        tryOpenFacebookApp(shareUrl);
        break;
      }
      case CONST.LINKEDIN: {
        const webUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        window.open(webUrl, '_blank', 'noopener,noreferrer');
        break;
      }
      case CONST.X: {
        const mobileUrl = `twitter://post?message=${encodeURIComponent(text + ' ' + shareUrl)}`;
        const webUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(
          shareUrl
        )}`;
        tryOpenMobileApp(mobileUrl, webUrl);
        break;
      }
      case CONST.EMAIL: {
        const mailtoUrl = `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(shareUrl)}`;
        window.location.href = mailtoUrl;
        break;
      }
      default:
        return;
    }
  }, [currentPodcast, selectedSharePlatform, shareUrl]);

  const tooltipProps = {
    enterDelay: 200,
    leaveDelay: 10,
    arrow: true,
    placement: 'top',
  };

  return (
    <>
      <Box className={classes.platformLinks}>
        <Tooltip title="Listen on Apple Podcasts" {...tooltipProps}>
          <IconButton
            component={Link}
            href={currentPodcast.appleUrl || currentPodcast.collectionViewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.platformButton}
          >
            {isLight ? <img src={AppleIconBlack} alt="Apple" /> : <img src={AppleIcon} alt="Apple" />}
          </IconButton>
        </Tooltip>
        {currentPodcast.spotifyUrl && (
          <Tooltip title="Listen on Spotify" {...tooltipProps}>
            <IconButton
              component={Link}
              href={currentPodcast.spotifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.platformButton}
            >
              {isLight ? (
                <img src={SpotifyIconBlack} alt="Spotify" />
              ) : (
                <img src={SpotifyIcon} alt="Spotify" />
              )}
            </IconButton>
          </Tooltip>
        )}
        <Box onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={classes.shareWrapper}>
          <Tooltip title="Share" {...tooltipProps}>
            <IconButton className={classes.shareButton}>
              <ShareIcon />
            </IconButton>
          </Tooltip>
          <Popper open={open} anchorEl={anchorEl} placement="bottom-start" className={classes.sharePopper}>
            <ClickAwayListener onClickAway={() => setOpen(false)}>
              <Paper className={classes.sharePaper}>
                <Box
                  className={classes.shareList}
                  onMouseEnter={() => setOpen(true)}
                  onMouseLeave={() => setOpen(false)}
                >
                  <Box onClick={() => handleShare(CONST.FACEBOOK)} className={classes.shareItem}>
                    <Box className={`${classes.socialIcon} ${classes.facebookIcon}`}>
                      <FacebookIcon />
                    </Box>
                    <Typography>Facebook</Typography>
                  </Box>
                  <Box onClick={() => handleShare(CONST.LINKEDIN)} className={classes.shareItem}>
                    <Box className={`${classes.socialIcon} ${classes.linkedinIcon}`}>
                      <LinkedInIcon />
                    </Box>
                    <Typography>LinkedIn</Typography>
                  </Box>
                  <Box onClick={() => handleShare(CONST.X)} className={classes.shareItem}>
                    <Box className={`${classes.socialIcon} ${classes.xIcon}`}>
                      <XIcon />
                    </Box>
                    <Typography>X.com</Typography>
                  </Box>
                  <Box onClick={() => handleShare(CONST.EMAIL)} className={classes.shareItem}>
                    <Box className={`${classes.socialIcon} ${classes.emailIcon}`}>
                      <EmailIcon />
                    </Box>
                    <Typography>Email</Typography>
                  </Box>
                  <Box className={classes.shareItem}>
                    <TextField
                      value={shareUrl}
                      variant="outlined"
                      size="small"
                      className={classes.linkField}
                      onClick={handleCopyLink}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <Box onClick={handleCopyLink} className={classes.copyButton}>
                                Copy
                              </Box>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Paper>
            </ClickAwayListener>
          </Popper>
        </Box>
        {showAnalyze ? (
          <Tooltip title="Charts" {...tooltipProps}>
            <IconButton className={classes.platformButton} onClick={handleChartsClick}>
              <TimelineIcon sx={{ color: isLight ? '#000' : '#fff' }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Analyze" {...tooltipProps}>
            <IconButton className={classes.platformButton} onClick={handleAnalyzeClick}>
              <img src={isLight ? BrainIconLight : BrainIconDark} alt="Analyze" style={{ width: isLight ? 30 : 40, height: isLight ? 30 : 40 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Snackbar
        open={showCopyNotification}
        autoHideDuration={2000}
        onClose={() => setShowCopyNotification(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        ContentProps={{
          sx: {
            backgroundColor: '#f56100',
            fontWeight: 600,
            fontSize: 15,
          },
        }}
      />

      <ShareAchievementModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        onShare={handleActualShare}
      />
    </>
  );
};
